import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  ApiBlueprint,
  createApiRef,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';

export interface Customer {
  name: string;
  code: string;
}

export interface GetOrCreateCustomerResult extends Customer {
  alreadyExists: boolean;
}

export interface CustomersApi {
  listCustomers(): Promise<Customer[]>;
  getOrCreateCustomer(name: string): Promise<GetOrCreateCustomerResult>;
}

export const customersApiRef = createApiRef<CustomersApi>().with({
  id: 'plugin.scaffolder.customer-registry.api',
});

export class CustomersClient implements CustomersApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  async listCustomers(): Promise<Customer[]> {
    // The customer-registry backend module mounts its route on the scaffolder plugin.
    const baseUrl = await this.discoveryApi.getBaseUrl('scaffolder');
    const response = await this.fetchApi.fetch(`${baseUrl}/customers`);

    if (!response.ok) {
      const body = await response.text();
      throw new Error(
        `Failed to fetch customers with status ${response.status}: ${body}`,
      );
    }

    return response.json() as Promise<Customer[]>;
  }

  async getOrCreateCustomer(name: string): Promise<GetOrCreateCustomerResult> {
    const baseUrl = await this.discoveryApi.getBaseUrl('scaffolder');
    const response = await this.fetchApi.fetch(`${baseUrl}/customers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });

    if (!response.ok) {
      const body = await response.text();
      throw new Error(
        `Failed to register customer with status ${response.status}: ${body}`,
      );
    }

    return response.json() as Promise<GetOrCreateCustomerResult>;
  }
}

export const customersApi = ApiBlueprint.make({
  name: 'customers',
  params: defineParams =>
    defineParams({
      api: customersApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
      },
      factory: ({ discoveryApi, fetchApi }) =>
        new CustomersClient(discoveryApi, fetchApi),
    }),
});
